import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SubmitButton } from "ui-components/Form";
import { Layout } from "ui-components/Layout";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { useCustomerValue } from "providers/CustomerContext";
import { formRender, initialValues, skuPackTypeValidation } from "./sku-pack-type-schema";
import SKUPackTypeInfo from "./components/SKUPackTypeInfo";
import { createSKUPackType } from "services/data.internal.service";
import { useCustWhSettingQuery, useVendorOptions, useSKUOptions } from "hooks/useQueryHook";
import { useLocation } from "react-router-dom";

const palletTypeOptions = {
  SMALL: { label: "Small Pallet", value: "SMALL" },
  STANDARD: { label: "Standard Pallet", value: "STANDARD" },
  LARGE: { label: "Large Pallet", value: "LARGE" },
};

function AddSKUPackType() {
  const { customerCode } = useCustomerValue();
  const location = useLocation();
  const { skuCode = null } = location?.state ?? {};
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState({
    ...initialValues,
    customerCode,
    skuCode,
    upmBase: 1,
    packType: "01",
    palletType: palletTypeOptions["STANDARD"],
  });
  const queryClient = useQueryClient();

  const [vendorOptions, setVendorOptions] = useState([]);
  const [skuOptions, setSKUOptions] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const createSKUPackTypeMutation = useMutation(createSKUPackType, {
    onSuccess: (data) => setAlert(<Success data={data} onChange={setAlert} />),
    onError,
  });
  const form = useForm({
    defaultValues: initialValues,
    values: formValues,
    resolver: yupResolver(skuPackTypeValidation),
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = form;

  const { isFetching } = useCustWhSettingQuery({
    customerCode,
    onSuccess: (data) => {
      if (data.data.data === undefined) {
        return;
      }
      const { upmBase, baseUOM, upmCase, caseUOM, upmPallet, palletUOM, palletType } =
        data.data.data;
      setValue("upmBase", upmBase);
      setValue("baseUOM", baseUOM);
      setValue("upmCase", upmCase);
      setValue("caseUOM", caseUOM);
      setValue("upmPallet", upmPallet);
      setValue("palletUOM", palletUOM);
      setValue("palletType", palletType ? palletTypeOptions[palletType] : null);
    },
    onError,
  });
  useVendorOptions({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;

      setVendorOptions(vendors);
      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0]);
      }
    },
    onError,
  });
  useSKUOptions({
    customerCode,
    onSuccess: (data) => setSKUOptions(data.data.data.map((x) => x.skuCode)),
  });

  const infoProps = {
    vendorOptions,
    skuOptions,
    control,
    errors,
    formRender,
  };
  const onSubmit = (data) => {
    window.scrollTo(0, 0);
    const validate = validateData(data);
    if (validate.length > 0) {
      setAlert(<Error_ content={validate.join(",")} onChange={setAlert} />);
      return;
    }
    createSKUPackTypeMutation.mutate(
      { ...data, palletType: data.palletType?.value },
      {
        onSuccess: () => {
          reset();
          queryClient.invalidateQueries(["cust-wh-setting"]);
          queryClient.invalidateQueries(["vendor-options"]);
        },
      }
    );
  };
  const createStatus = createSKUPackTypeMutation.status === "loading";
  return (
    <Layout lg={12}>
      {isFetching && <LoadingDialog title="Fetching Customer Warehouse Setting" />}
      {createStatus && <LoadingDialog />}
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SKUPackTypeInfo {...infoProps} />
        <SubmitButton label="submit" disabled={isSubmitting} url={-1} />
      </form>
    </Layout>
  );
}

export default AddSKUPackType;

export function validateData(data) {
  const {
    innerpackUOM,
    upmInnerpack,
    caseUOM,
    upmCase,
    palletUOM,
    upmPallet,
    grossWeightUOM,
    upmGrossWeight,
    netWeightUOM,
    upmNetWeight,
    cubicUOM,
    upmCubic,
  } = data;
  let message = [];
  if (upmInnerpack > 0 && innerpackUOM === null) message.push("Invalid Inner Pack Measurement");
  if (upmCase > 0 && caseUOM === null) message.push("Invalid Case Measurement");
  if (upmPallet > 0 && palletUOM === null) message.push("Invalid Pallet Measurement");
  if (upmGrossWeight > 0 && grossWeightUOM === null)
    message.push("Invalid Gross Weight Measurement");
  if (upmNetWeight > 0 && netWeightUOM === null) message.push("Invalid Net Weight Measurement");
  if (upmCubic > 0 && cubicUOM === null) message.push("Invalid Cubic Measurement");

  if (upmInnerpack === 0 && innerpackUOM !== null) message.push("Invalid Inner Pack Measurement");
  if (upmCase === 0 && caseUOM !== null) message.push("Invalid Case Measurement");
  if (upmPallet === 0 && palletUOM !== null) message.push("Invalid Pallet Measurement");
  if (upmGrossWeight === 0 && grossWeightUOM !== null)
    message.push("Invalid Gross Weight Measurement");
  if (upmNetWeight === 0 && netWeightUOM !== null) message.push("Invalid Net Weight Measurement");
  if (upmCubic === 0 && cubicUOM !== null) message.push("Invalid Cubic Measurement");

  return message;
}
